"use client";

/**
 * LiveAvatarCoach — SSR-safe shell.
 *
 * This is the public entry point of the shared widget (Amendment 7: subpath
 * import only, NOT exported from `packages/ui/index.tsx`).
 *
 * Hydration invariant (Amendment 8, verbatim): "The collapsed launcher renders
 * identical markup during SSR and the client's first render. Rendering must not
 * branch on `window`. After mount and only after user expansion, dynamically
 * import the SDK runtime and start a session."
 *
 * Enforcement:
 *  - This module imports ONLY SDK-free presentational modules (`./types`,
 *    `./launcher`) plus `React.lazy` for the runtime. It never imports
 *    `@heygen/liveavatar-web-sdk` and never branches on `window`.
 *  - `useIsMobile()` returns `false` on first render (both server AND client),
 *    so the launcher-only first render is byte-identical across SSR and client.
 *    The desktop↔mobile swap happens only after mount (post-effect update).
 *  - `LiveAvatarRuntime` (the only SDK importer) is `React.lazy`-loaded and
 *    rendered only after the user expands, wrapped in `<Suspense>` so the
 *    chunk-loading state is a visible designed surface (`chunk-loading`).
 *
 * Session start requires post-mount state and a user-initiated session. The
 * initial mint follows an explicit launcher/card click; the one-shot
 * auto-resume path may mint without a fresh gesture because it resumes that
 * same user-initiated, still-mounted conversation.
 *
 * All copy flows through the `copy` prop — no hardcoded user-facing strings.
 */
import * as React from "react";
import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";

import { Skeleton } from "../..";
import { useIsMobile } from "../../hooks/use-mobile";
import { cn } from "../../lib/utils";

import { LauncherButton } from "./launcher";
import type {
  LiveAvatarCoachHandle,
  LiveAvatarCoachProps,
  LiveAvatarSessionRequest,
} from "./types";

/**
 * Public type surface, re-exported from the entry. The `…/live-avatar-coach/types`
 * subpath resolves through an extensionless exports-map wildcard that tsc rejects
 * under `moduleResolution: "Bundler"`, so consumers import these from here —
 * this entry's exports target names a real file. MUST stay `export type`:
 * type-only exports are erased at compile time, so this adds no runtime import
 * and cannot regress the SSR/hydration invariant documented above.
 */
export type {
  LiveAvatarCoachCopy,
  LiveAvatarCoachStatus,
  LiveAvatarCoachEndReason,
  LiveAvatarSessionToken,
  LiveAvatarSessionRequest,
  LiveAvatarCoachHandle,
  LiveAvatarCoachReport,
  LiveAvatarCoachReportPhase,
  LiveAvatarCoachReportOutcome,
  LiveAvatarClientEvent,
  LiveAvatarClientEventCode,
  LiveAvatarCoachProps,
} from "./types";

/**
 * The runtime (SDK importer) is lazy-loaded so its chunk — and the SDK chunk
 * it in turn dynamically imports — are fetched only on first expand.
 */
const LiveAvatarRuntime = React.lazy(
  () => import("./use-live-avatar-session").then((m) => ({ default: m.LiveAvatarRuntime })),
);

/**
 * Chunk-loading fallback — a panel-shaped skeleton rendered by the shell while
 * the runtime chunk is fetched, so chunk-loading is a VISIBLE state (UI Brief),
 * not a blank flash. SDK-free; mirrors the desktop/mobile panel geometry.
 */
function ChunkLoadingFallback({
  copy,
  mobile,
  panelOffsetClassName,
}: {
  copy: LiveAvatarCoachProps["copy"];
  mobile: boolean;
  panelOffsetClassName?: string;
}) {
  // Full-bleed skeleton stage (M3) — fills the panel so chunk-loading is a
  // visible designed surface that matches the live stage geometry.
  const body = (
    <>
      <Skeleton className="h-full min-h-[240px] w-full flex-1 rounded-none bg-black" />
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        <span aria-live="polite">{copy.loading}</span>
      </div>
    </>
  );

  if (mobile) {
    // Minimal bottom-sheet-shaped skeleton (no Radix wiring needed for a fallback).
    return (
      <div
        role="dialog"
        aria-label={copy.panelTitle}
        className="fixed inset-x-0 bottom-0 z-40 flex h-[85dvh] max-h-[85dvh] flex-col gap-0 rounded-t-xl border bg-card text-card-foreground pb-[env(safe-area-inset-bottom)] shadow-lg"
      >
        <div className="border-b px-4 py-3 text-sm font-semibold">{copy.panelTitle}</div>
        {body}
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-label={copy.panelTitle}
      className={cn(
        "fixed bottom-4 right-12 z-40 flex h-[min(600px,calc(100dvh-32px))] max-h-[calc(100dvh-32px)] w-[440px] max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden rounded-xl border bg-card text-card-foreground shadow-xs",
        "[bottom:calc(1rem+env(safe-area-inset-bottom))] [right:calc(3rem+env(safe-area-inset-right))]",
        panelOffsetClassName,
      )}
    >
      <div className="border-b bg-foreground/5 px-4 py-3 text-sm font-semibold">
        {copy.panelTitle}
      </div>
      {body}
    </div>
  );
}

/**
 * Layout effect on the client, plain effect on the server (where layout effects
 * warn and never run). Used to resolve rail slots BEFORE paint so the
 * launcher never flashes in the bottom-right corner on its way to the header.
 * This is a module-level capability check, not a render-time `window` branch —
 * the hydration invariant in the file header still holds.
 */
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

/**
 * Resolves `selector` to the live set of launcher slot elements.
 *
 * Returns `null` until the first scan has run — the caller renders no launcher
 * at all in that state, so there is no floating-pill flash before the portal
 * target is known. Re-scans on DOM mutations (coalesced to one rAF) so a slot
 * that mounts, unmounts, or moves with the app's own render cycle is picked up
 * without the app having to notify the widget.
 */
function useLauncherSlots(selector: string | undefined): Element[] | null {
  const [slots, setSlots] = React.useState<Element[] | null>(null);

  useIsomorphicLayoutEffect(() => {
    if (!selector) {
      setSlots(null);
      return;
    }

    let frame = 0;
    const scan = () => {
      const found = Array.from(document.querySelectorAll(selector));
      // Preserve referential identity when nothing changed — otherwise every
      // unrelated DOM mutation would re-render (and so remount, killing the
      // pulse animation on) the portalled launcher.
      setSlots((prev) =>
        prev && prev.length === found.length && prev.every((node, i) => node === found[i])
          ? prev
          : found,
      );
    };

    scan();

    const observer = new MutationObserver(() => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        scan();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [selector]);

  return slots;
}

export const LiveAvatarCoach = React.forwardRef<LiveAvatarCoachHandle, LiveAvatarCoachProps>(
  function LiveAvatarCoach(
    {
      getSessionToken,
      reportSession,
      reportClientEvent,
      copy,
      onError,
      className,
      avatarSrc,
      pageContext,
      hideLauncher,
      panelOffsetClassName,
      trapFocus,
      onOpenChange,
      restoreFocusTo,
      launcherSlotSelector,
    },
    ref,
  ) {
    const [expanded, setExpanded] = React.useState(false);
    // Ref to the launcher button so Escape / close can restore focus to it
    // (UI Brief a11y: "Escape closes + restores focus to launcher"). The launcher
    // remounts on collapse, so the ref is read inside an effect that runs after
    // React commits the re-render (when the launcher is back in the DOM).
    const launcherRef = React.useRef<HTMLButtonElement | null>(null);
    // Tab launchers are portalled once per matching rail slot; an app could
    // render more than one (or hide one per breakpoint), so focus restore has to
    // pick the visible one rather than simply the first.
    const tabRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
    const prevExpandedRef = React.useRef(false);
    // Wrapper around the expanded panel — the shell-owned focus target for
    // `focus()` / re-open-while-expanded. The dialog itself lives inside the
    // lazy runtime chunk, which the shell has no ref into.
    const panelWrapRef = React.useRef<HTMLDivElement | null>(null);

    // The session request FROZEN at open (Amendment 1-E). Only ever mutated in
    // the same event that flips `expanded`, so its value is stable for the whole
    // lifetime of an expanded runtime — reading it during render below is
    // deterministic. Launcher opens freeze `undefined`; re-open while expanded
    // does NOT replace it (that path is a focus()); every close path clears it.
    const sessionRequestRef = React.useRef<LiveAvatarSessionRequest | undefined>(undefined);

    // Render-mirror refs so the imperative handle and the close-transition
    // effects always read the latest values without re-creating per render.
    // `expandedRef` is ALSO written synchronously by every open/close path
    // (review F2): under React batching, `open(A); open(B)` in one tick would
    // otherwise both read the stale pre-render value and let B replace A's
    // frozen request. The render-time mirror stays as a backstop — the state
    // only ever changes through those same paths, so the two writes agree.
    const expandedRef = React.useRef(expanded);
    expandedRef.current = expanded;
    const restoreFocusToRef = React.useRef(restoreFocusTo);
    restoreFocusToRef.current = restoreFocusTo;
    const onOpenChangeRef = React.useRef(onOpenChange);
    onOpenChangeRef.current = onOpenChange;

    // useIsMobile() returns false on first render (SSR + client) — the launcher-
    // only first render is identical server/client. The mobile swap happens only
    // after mount (post-effect update), preserving the hydration invariant.
    const isMobile = useIsMobile();

    const focusPanel = React.useCallback(() => {
      panelWrapRef.current?.focus({ preventScroll: true });
    }, []);

    const handleExpand = React.useCallback(() => {
      // User gesture → safe to start a session (post-mount + interaction both hold).
      // Launcher opens freeze `undefined` (default-mode mint).
      sessionRequestRef.current = undefined;
      expandedRef.current = true;
      setExpanded(true);
    }, []);

    const handleClose = React.useCallback(() => {
      // Every close path (panel close button, error-close, Escape) clears the
      // frozen request. The runtime unmounts in the same commit, so it never
      // observes the cleared value.
      sessionRequestRef.current = undefined;
      expandedRef.current = false;
      setExpanded(false);
    }, []);

    React.useImperativeHandle(
      ref,
      () => ({
        open: (request?: LiveAvatarSessionRequest) => {
          if (expandedRef.current) {
            // Re-open while expanded behaves as focus() — the frozen request
            // is NOT replaced (the live session was minted against it). The
            // synchronous ref write below makes this hold even for a second
            // open() batched into the same tick as the first (review F2).
            focusPanel();
            return;
          }
          sessionRequestRef.current = request;
          expandedRef.current = true;
          setExpanded(true);
        },
        focus: focusPanel,
      }),
      [focusPanel],
    );

    // Escape closes + restores focus (UI Brief). Attached only when expanded.
    React.useEffect(() => {
      if (!expanded) return;
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          e.preventDefault();
          handleClose();
        }
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }, [expanded, handleClose]);

    // Restore focus after a close transition: caller-registered element →
    // launcher (when rendered) → no-op. Runs after React commits the collapse
    // re-render (the launcher, if any, is back in the DOM). The caller callback
    // is try/caught and a disconnected element falls through to the launcher —
    // the chain never throws on unmounted nodes. The launcher may be either
    // the floating pill (launcherRef) or a portalled rail tab (tabRefs);
    // `offsetParent === null` for a display:none slot (a hidden breakpoint
    // twin), so the tab fallback picks the one the user can actually see.
    React.useEffect(() => {
      if (prevExpandedRef.current && !expanded) {
        let target: HTMLElement | null = null;
        try {
          target = restoreFocusToRef.current?.() ?? null;
        } catch {
          target = null;
        }
        if (target?.isConnected) {
          target.focus({ preventScroll: true });
        } else {
          const tabbed = tabRefs.current.find(
            (node) => node && node.offsetParent !== null,
          );
          (launcherRef.current ?? tabbed)?.focus({ preventScroll: true });
        }
      }
      prevExpandedRef.current = expanded;
    }, [expanded]);

    // Notify the app of expanded-state transitions (open, close, Escape,
    // error-close) — in an effect, never during render, and never for the
    // initial collapsed mount.
    const notifiedExpandedRef = React.useRef(false);
    React.useEffect(() => {
      if (notifiedExpandedRef.current === expanded) return;
      notifiedExpandedRef.current = expanded;
      onOpenChangeRef.current?.(expanded);
    }, [expanded]);

    // Rail slots, when the app supplies a selector. `null` = not scanned yet.
    const slots = useLauncherSlots(launcherSlotSelector);
    // No selector, or a selector that matched nothing — fall back to the
    // original floating pill so the coach is never unreachable.
    const useFloating = !launcherSlotSelector || (slots !== null && slots.length === 0);

    const renderLauncher = (
      variant: "floating" | "tab",
      launcherButtonRef?: React.Ref<HTMLButtonElement>,
    ) => (
      <LauncherButton
        ref={launcherButtonRef}
        variant={variant}
        label={copy.launcherLabel}
        available
        avatarSrc={avatarSrc}
        expanded={expanded}
        onClick={handleExpand}
      />
    );

    return (
      <div className={className}>
        {/* Collapsed launcher — rendered on SSR and first client render identically
            (or nothing at all under `hideLauncher`, also SSR-identical — it wins
            over `launcherSlotSelector`). Mutually exclusive with the expanded
            panel (UI Brief: collapsed bubble ↔ expanded live-video panel).
            Portalled into the app's rail slot when one exists; otherwise the
            original fixed bottom-right pill. */}
        {!expanded ? (
          hideLauncher ? null : useFloating ? (
            renderLauncher("floating", launcherRef)
          ) : (
            (slots ?? []).map((slot, i) =>
              createPortal(
                renderLauncher("tab", (node) => {
                  tabRefs.current[i] = node;
                }),
                slot,
                `coach-launcher-${i}`,
              ),
            )
          )
        ) : (
          <div ref={panelWrapRef} tabIndex={-1} className="outline-none">
            <React.Suspense
              fallback={
                <ChunkLoadingFallback
                  copy={copy}
                  mobile={isMobile}
                  panelOffsetClassName={panelOffsetClassName}
                />
              }
            >
              <LiveAvatarRuntime
                copy={copy}
                getSessionToken={getSessionToken}
                reportSession={reportSession}
                reportClientEvent={reportClientEvent}
                onError={onError}
                onClose={handleClose}
                mobile={isMobile}
                pageContext={pageContext}
                sessionRequest={sessionRequestRef.current}
                panelOffsetClassName={panelOffsetClassName}
                trapFocus={trapFocus}
              />
            </React.Suspense>
          </div>
        )}
      </div>
    );
  },
);

export default LiveAvatarCoach;
